import { copiableProjection } from "./projection-helpers";

// The rill loader caches under this prefix inside the session document.
// A coach with thousands of cycles and full exercise arrays pushes that
// document past the BSON size limit, so these keys are never written again
// and any leftover copy is removed before the session is saved.
const COPIABLE_CYCLES_SESSION_PREFIX = "@rill/loader/copiableCycles";

export function dropCopiableCyclesFromSession(session) {
  if (!session?.items) return;
  const keys = [];
  for (const item of session.items) {
    if (
      item?.key &&
      String(item.key).startsWith(COPIABLE_CYCLES_SESSION_PREFIX)
    ) {
      keys.push(item.key);
    }
  }
  for (const key of keys) session.delete(key);
}

// Lives on locals for this request only. Not registered with the loader, so
// it is not written into the session.
export async function loadCopiableCycles({ api, locals }, owner) {
  locals.copiableCyclesError = false;
  try {
    const response = await api("cycle", {
      query: {
        limit: false,
        createdBy: locals.activeUserToRequest._id,
        status: { $in: ["created", "draft", "published"] },
        type: { $in: ["coach", "subscription"] },
        owner: owner || locals.activeUserToRequest._id,
        isDeleted: { $ne: true },
        "programs.0": { $exists: true },
        customSort: { createdDate: -1 },
        customProjection: copiableProjection,
      },
    });
    if (!response.ok) {
      console.error("copiable cycles request failed", response.status);
      locals.copiableCycles = [];
      locals.copiableCyclesError = true;
      return locals.copiableCycles;
    }
    const cycles = await response.json();
    if (!Array.isArray(cycles)) {
      console.error("copiable cycles response was not a list");
      locals.copiableCycles = [];
      locals.copiableCyclesError = true;
      return locals.copiableCycles;
    }
    locals.copiableCycles = cycles;
    return locals.copiableCycles;
  } catch (error) {
    console.error("copiable cycles request failed", error);
    locals.copiableCycles = [];
    locals.copiableCyclesError = true;
    return locals.copiableCycles;
  }
}

// Mirrors the empty-workout assert in actions/cycle.js send:
// a block with no sessions, or a session with no exercises, cannot be copied.
// [].every(...) is true, so an empty sessions list has to be rejected on its own.

export function programHasNoEmptyWorkouts(program) {
  const sessions = program?.sessions;
  if (!Array.isArray(sessions) || sessions.length === 0) return false;
  return sessions.every(
    (session) =>
      Array.isArray(session?.exercises) && session.exercises.length > 0
  );
}

// Block numbers are not a dense index. Cycles can skip orders (34, 35, 37),
// and the select stores the order (sometimes as a string).
export function cycleOwnerId(cycle) {
  const owner = cycle?.owner;
  if (owner == null || owner === "") return "";
  if (typeof owner === "object") {
    return owner._id == null ? "" : String(owner._id);
  }
  return String(owner);
}

export function programByOrder(programs, programOrder) {
  if (!Array.isArray(programs) || programOrder == null || programOrder === "") {
    return undefined;
  }
  const order = String(programOrder);
  return programs.find((program) => String(program?.order) === order);
}

export function cycleHasNoEmptyWorkouts(cycle) {
  const programs = cycle?.programs;
  if (!Array.isArray(programs) || programs.length === 0) return false;
  return programs.every(programHasNoEmptyWorkouts);
}

export function cycleHasCopyableBlock(cycle) {
  const programs = cycle?.programs;
  if (!Array.isArray(programs) || programs.length === 0) return false;
  return programs.some(programHasNoEmptyWorkouts);
}
